import { FilterMode } from "@/shared/models/filters";
import clsx from "clsx";
import { Link } from "react-router-dom";
import { usePortalContainer } from "@/shared/context/PortalContext/usePortalContext";

interface FilterModeSelectorProps {
  filterMode: FilterMode;
  onFilterModeChange: (mode: FilterMode) => void;
  disabled?: boolean;
}

export const FilterModeSelector = ({
  filterMode,
  onFilterModeChange,
  disabled,
}: FilterModeSelectorProps) => {
  return (
    <div
      className={clsx(
        "space-y-2 mt-2",
        disabled && "opacity-50 pointer-events-none"
      )}
    >
      <div className="text-[10px] font-medium text-gray-500 uppercase tracking-wide">
        Combine by
      </div>
      <div className="flex gap-2">
        <button
          onClick={(e) => {
            e.stopPropagation();
            onFilterModeChange("OR");
          }}
          className={`flex-1 px-2.5 py-1.5 text-[11px] font-medium rounded border transition-colors ${
            filterMode === "OR"
              ? "border-squawk-blue bg-squawk-blue/20 text-white"
              : "border-gray-700 bg-gray-800/50 text-gray-400 hover:border-gray-600"
          }`}
        >
          Any
        </button>
        <button
          onClick={(e) => {
            e.stopPropagation();
            onFilterModeChange("AND");
          }}
          className={`flex-1 px-2.5 py-1.5 text-[11px] font-medium rounded border transition-colors ${
            filterMode === "AND"
              ? "border-squawk-blue bg-squawk-blue/20 text-white"
              : "border-gray-700 bg-gray-800/50 text-gray-400 hover:border-gray-600"
          }`}
        >
          All
        </button>
      </div>
    </div>
  );
};

interface CreateNewLinkProps {
  url: string;
  label?: string;
}

export const CreateNewLink = ({ url, label = "Create new..." }: CreateNewLinkProps) => {
  const portalContainer = usePortalContainer();

  // Don't show this link in popout windows - creation needs full screen context
  const isPopout = portalContainer !== null;
  if (isPopout) {
    return null;
  }

  return (
    <Link
      to={url}
      onClick={(e) => e.stopPropagation()}
      className="text-[10px] text-squawk-blue hover:text-squawk-blue-dark transition-colors"
    >
      {label}
    </Link>
  );
};

export const ManageFiltersButton = () => {
  const portalContainer = usePortalContainer();

  // Don't show this button in popout windows - filter management needs full screen context
  const isPopout = portalContainer !== null;
  if (isPopout) {
    return null;
  }

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    // Use window.location for navigation to avoid router dependency
    window.location.href = "/watchlists-filters";
  };

  return (
    <div className="space-y-2 mt-2">
      <button
        onClick={handleClick}
        className="w-full px-2.5 py-1.5 text-[11px] font-medium rounded border border-gray-700 bg-gray-800/50 text-gray-400 hover:border-squawk-blue hover:text-squawk-blue transition-colors"
      >
        Manage Advanced Filters
      </button>
    </div>
  );
};

interface ClearSelectionsFooterProps {
  clearSelections?: () => void;
  disableClear?: boolean;
}

export const ClearSelectionsFooter = ({
  clearSelections,
  disableClear,
}: ClearSelectionsFooterProps) => {
  // Don't render anything if there are no selections to clear
  if (disableClear) {
    return null;
  }

  return (
    <button
      onClick={(e) => {
        e.stopPropagation();
        clearSelections?.();
      }}
      className="w-full px-2.5 py-1.5 text-[11px] font-medium rounded border border-red-600/50 bg-red-600/20 text-red-400 hover:border-red-500 hover:bg-red-600/30 hover:text-red-300 transition-colors"
    >
      Clear All Selections
    </button>
  );
};
